import { useMemo, useState } from 'react';
import { Button } from '@design-system/components/button/button';
import { PlainModal } from '@design-system/components/modal/modal';
import { Typography } from '@design-system/components/typography/typography';
import {
  ScheduleGroup,
  ScheduledWorkout,
  toExistingScheduleTimes,
} from '../model/scheduleGroups.domain';
import { useJoinedTime } from '../model/useJoinScheduleGroup';
import { SchedulePicker } from './SchedulePicker';
import {
  modalBody,
  publishDateModal,
  publishDateModalWide,
  row,
} from './scheduleGroups.css';

type PublishDateModalProps = {
  title: string;
  group: ScheduleGroup<ScheduledWorkout>;
  scheduled: Array<ScheduledWorkout>;
  programName: string | undefined;
  onClose: () => void;
  onConfirm: (publishedAt: string) => void;
};

const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const PublishDateModal = (p: PublishDateModalProps) => {
  const [publishedAt, setPublishedAt] = useState(p.group.publishedAt);
  const [timeZone, setTimeZone] = useState(localTimeZone);
  const times = useMemo(
    () =>
      toExistingScheduleTimes(
        p.scheduled,
        p.group.items.map((item) => item.id),
        new Date(),
      ),
    [p.scheduled, p.group],
  );
  const { joined, join, clear } = useJoinedTime(
    times,
    publishedAt,
    setPublishedAt,
  );

  return (
    <PlainModal
      isOpen
      onClose={p.onClose}
      modalClassName={
        times.length > 0 ? publishDateModalWide : publishDateModal
      }
    >
      <div className={modalBody}>
        <Typography>{p.title}</Typography>
        <SchedulePicker
          publishedAt={publishedAt}
          timeZone={timeZone}
          onChange={setPublishedAt}
          onTimeZoneChange={setTimeZone}
          times={times}
          joined={joined}
          onJoin={join}
          onClear={clear}
          programName={p.programName}
          joiningCount={p.group.items.length}
        />
        <Typography variant="body" size="small">
          Every workout goes live at this time, in the order shown.
        </Typography>
        <div className={row}>
          <Button variant="text" color="neutral" onClick={p.onClose}>
            Cancel
          </Button>
          <Button
            color="neutral"
            disabled={Date.parse(publishedAt) <= Date.now()}
            onClick={() => p.onConfirm(publishedAt)}
            testId="confirmGroupSchedule"
          >
            Schedule
          </Button>
        </div>
      </div>
    </PlainModal>
  );
};
